import { createTheme, ThemeOptions } from '@mui/material/styles';
import { PaletteOptions } from '@mui/material';

// カスタムカラーとコンポーネントの型定義
declare module '@mui/material/styles' {
  interface Palette {
    smartSkillTalk: {
      orange: string;
      blue: string;
      red: string;
      green: string;
      lightBlue: string;
      black: string;
      gray: string;
      darkGray: string;
      textDark: string;
      textLight: string;
      linkBlue: string;
      aiBlue: string;
    };
  }
  
  interface PaletteOptions {
    smartSkillTalk?: {
      orange?: string;
      blue?: string;
      red?: string;
      green?: string;
      lightBlue?: string;
      black?: string;
      gray?: string;
      darkGray?: string;
      textDark?: string;
      textLight?: string;
      linkBlue?: string;
      aiBlue?: string;
    };
  }

  interface Components {
    MuiBox?: {
      styleOverrides?: {
        root?: any;
      };
    };
  }
}

// SmartSkillTalkのブランドカラー
const brandColors = {
  orange: '#FFA500', // ロゴのオレンジ部分
  blue: '#003A70',   // ロゴの青部分
  red: '#D32F2F',    // ロゴの赤部分
  lightBlue: '#F0F7FF', // 背景の薄い青（Figmaデザインに合わせて更新）
  green: '#5EB600',  // サインインボタンの緑（Figmaデザインに合わせて更新）
  black: '#000000',  // フッターの黒
  gray: '#999999',   // 入力フィールドのプレースホルダー色
  darkGray: '#666666', // フッターの背景色
  textDark: '#333333', // 入力テキストの色
  textLight: '#CCCCCC', // フッターのコピーライトテキスト色
  linkBlue: '#25507A', // リンクの青色
  aiBlue: '#6C9AD7', // AIアイコンの青色
};

// カスタムテーマの作成
const theme = createTheme({
  palette: {
    primary: {
      main: brandColors.blue,
      light: brandColors.aiBlue,
      dark: '#002548',
      contrastText: '#fff',
    },
    secondary: {
      main: brandColors.orange,
      light: '#FFB74D',
      dark: '#E67700',
      contrastText: '#fff',
    },
    error: {
      main: brandColors.red,
    },
    warning: {
      main: '#ffa000',
    },
    info: {
      main: '#0288d1',
    },
    success: {
      main: brandColors.green,
    },
    background: {
      default: brandColors.lightBlue,
      paper: '#ffffff',
    },
    // カスタムカラーを追加
    smartSkillTalk: {
      orange: brandColors.orange,
      blue: brandColors.blue,
      red: brandColors.red,
      green: brandColors.green,
      lightBlue: brandColors.lightBlue,
      black: brandColors.black,
      gray: brandColors.gray,
      darkGray: brandColors.darkGray,
      textDark: brandColors.textDark,
      textLight: brandColors.textLight,
      linkBlue: brandColors.linkBlue,
      aiBlue: brandColors.aiBlue,
    },
  },
  typography: {
    fontFamily: [
      '"Meiryo"', // Windows用の日本語フォント
      '"Hiragino Kaku Gothic ProN"', // macOS/iOS用の日本語フォント
      '"Hiragino Sans"', // iOS 9以降の標準日本語フォント
      '"Yu Gothic Medium"', // Windows 8.1以降の日本語フォント
      '"Noto Sans JP"', // Google Fontsの日本語フォント
      '"Roboto"',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
        },
        containedPrimary: {
          backgroundColor: brandColors.green,
          '&:hover': {
            backgroundColor: '#86CA04',
          },
        },
        containedError: {
          backgroundColor: brandColors.red,
          '&:hover': {
            backgroundColor: '#f04848',
          },
        },
          // サインインボタン用のスタイル
        containedSuccess: {
          padding: '10px 80px',
          backgroundColor: brandColors.green,
          border: 'none',
          borderRadius: '6px',
          fontFamily: [
            '"Meiryo"',
            '"Hiragino Kaku Gothic ProN"',
            '"Hiragino Sans"',
            '"Yu Gothic Medium"',
            '"Noto Sans JP"',
            '"Roboto"',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          fontSize: '18px',
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: '0.12em',
          textAlign: 'center',
          color: '#FFFFFF',
          cursor: 'pointer',
          transition: 'background-color 0.3s',
          '&:hover': {
            backgroundColor: '#86CA04',
          }
        }
      },
      variants: [
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            backgroundColor: brandColors.green,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#86CA04',
            }
          }
        },
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            backgroundColor: brandColors.blue,
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#3687DF',
            }
          }
        },
      ]
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
          // 標準入力フィールド用のスタイル
          '& .MuiInputBase-input': {
            fontFamily: [
              '"Noto Sans JP"',
              '"Hiragino Kaku Gothic ProN"',
              '"Hiragino Sans"',
              '"Yu Gothic Medium"',
              '"Noto Sans JP"',
              '"Roboto"',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif',
            ].join(','),
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.12em',
            color: brandColors.textDark,
            '&::placeholder': {
              color: brandColors.gray,
              opacity: 1,
            }
          }
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: '0.12em',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    // フォーム入力フィールド用のカスタムスタイル
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontFamily: [
            '"Meiryo"',
            '"Hiragino Kaku Gothic ProN"',
            '"Hiragino Sans"',
            '"Yu Gothic Medium"',
            '"Noto Sans JP"',
            '"Roboto"',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: '0.12em',
        },
        input: {
          '&::placeholder': {
            color: brandColors.gray,
            opacity: 1,
          }
        }
      }
    },
    // フッターリンク用のスタイル
    MuiBox: {
      styleOverrides: {
        root: {
          '&.footer-link': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.12em',
            textAlign: 'center',
            color: '#FFFFFF',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          },
          '&.copyright-text': {
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.12em',
            textAlign: 'center',
            color: brandColors.textLight,
          },
          '&.catch-phrase-text': {
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.12em',
            textAlign: 'center',
            color: brandColors.linkBlue,
            whiteSpace: 'nowrap',
          },
          '&.forgot-password-link': {
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.12em',
            color: brandColors.linkBlue,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          }
        }
      }
    }
  },
});

export default theme;
